import { ArrowRight, Check, CircleAlert, Lock, PartyPopper } from 'lucide-react'
import { Button } from '@/components/occurrences/button'
import { cn } from '@/lib/utils'
import {
	type ProgressStage,
	SECTIONS,
	type SectionKey,
	type Transition,
} from '@/mocks/occurrences/rules'
import type { MockProfile } from '@/types/occurrence'
import { PROFILE_LABEL, STATUS_LABEL } from '@/types/occurrence'

/** Stepper das etapas da ocorrência (só as aplicáveis): feito, atual e pendente; cada etapa leva à tela. */
export const StageStepper = ({
	stages,
	percent,
	onGo,
}: {
	stages: ProgressStage[]
	percent: number
	onGo: (section: SectionKey) => void
}) => {
	const currentKey = stages.find((s) => !s.done)?.key
	return (
		<div className="mt-3 flex items-start gap-3">
			<ol className="flex min-w-0 flex-1 overflow-x-auto pt-1 pb-1">
				{stages.map((s, i) => {
					const state = s.done
						? 'done'
						: s.key === currentKey
							? 'current'
							: 'pending'
					const prevDone = i > 0 && stages[i - 1].done
					return (
						<li
							key={s.key}
							className="relative min-w-[84px] flex-1 first:min-w-[64px]"
						>
							{i > 0 && (
								<span
									aria-hidden="true"
									className={cn(
										'absolute top-3.5 right-1/2 left-[calc(-50%+14px)] h-0.5 -translate-y-1/2',
										prevDone
											? 'bg-kaefer-grass-green'
											: 'bg-kaefer-light-silver',
									)}
									style={{ right: 'calc(50% + 14px)' }}
								/>
							)}
							<button
								type="button"
								onClick={() => onGo(s.section)}
								title={`${s.label}${s.done ? ' · concluída' : ' · pendente'} (peso ${s.weight})`}
								className="group relative z-10 flex w-full flex-col items-center gap-1"
							>
								<span
									className={cn(
										'flex size-7 items-center justify-center rounded-full border-2 text-xs font-semibold transition-colors',
										state === 'done' &&
											'border-kaefer-grass-green bg-kaefer-grass-green text-white',
										state === 'current' &&
											'border-kaefer-dark-grey bg-kaefer-dark-grey text-white ring-4 ring-kaefer-dark-grey/15',
										state === 'pending' &&
											'border-kaefer-light-silver bg-card text-muted-foreground group-hover:border-kaefer-dark-grey',
									)}
								>
									{state === 'done' ? <Check className="size-4" /> : i + 1}
								</span>
								<span
									className={cn(
										'px-1 text-center text-[11px] leading-tight',
										state === 'current'
											? 'font-semibold text-kaefer-dark-grey'
											: state === 'done'
												? 'text-kaefer-dark-grey'
												: 'text-muted-foreground',
									)}
								>
									{s.label}
								</span>
							</button>
						</li>
					)
				})}
			</ol>
			<div className="shrink-0 pt-0.5 text-right">
				<span className="block text-2xl leading-none font-bold text-kaefer-dark-grey tabular-nums">
					{percent}%
				</span>
				<span className="text-[10px] text-muted-foreground">concluído</span>
			</div>
		</div>
	)
}

const sectionLabel = (k: SectionKey) =>
	SECTIONS.find((s) => s.key === k)?.label ?? k

/** Painel "Próximo passo": diz o que falta para avançar, leva à tela certa e oferece o botão de avançar. */
export const NextStepBar = ({
	transition,
	profile,
	onGo,
	onRun,
}: {
	transition: Transition
	profile: MockProfile
	onGo: (section: SectionKey) => void
	onRun: (t: Transition) => void
}) => {
	const { blockers, allowed, deniedByProfile } = transition
	const ready = allowed
	const title = deniedByProfile
		? `Aguardando: ${transition.who}`
		: blockers.length > 0
			? `Falta resolver ${blockers.length} ${blockers.length === 1 ? 'item' : 'itens'} para avançar`
			: 'Tudo pronto para avançar'

	return (
		<section
			className={cn(
				'rounded-xl border border-l-4 bg-card p-4 shadow-sm',
				ready
					? 'border-l-kaefer-grass-green'
					: deniedByProfile && blockers.length === 0
						? 'border-l-kaefer-silver'
						: 'border-l-kaefer-honey-yellow',
			)}
		>
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="flex items-start gap-3">
					<span
						className={cn(
							'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full',
							ready
								? 'bg-kaefer-grass-green/15 text-kaefer-forest-green'
								: 'bg-kaefer-honey-yellow/20 text-kaefer-pumpkin-orange',
						)}
					>
						{ready ? (
							<PartyPopper className="size-4" />
						) : (
							<CircleAlert className="size-4" />
						)}
					</span>
					<div>
						<p className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
							Próximo passo · {STATUS_LABEL[transition.to]}
						</p>
						<h2 className="text-base font-semibold text-kaefer-dark-grey">
							{title}
						</h2>
					</div>
				</div>
				<Button
					variant="primary"
					className="gap-2 font-semibold"
					disabled={!allowed}
					onClick={() => onRun(transition)}
				>
					{!allowed && <Lock className="size-4" />}
					{transition.label}
				</Button>
			</div>

			{blockers.length > 0 && (
				<ul className="mt-3 divide-y rounded-lg border">
					{blockers.map((b) => (
						<li
							key={b.text}
							className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
						>
							<span className="flex items-center gap-2 text-kaefer-dark-grey">
								<span className="size-2 shrink-0 rounded-full bg-kaefer-honey-yellow" />
								{b.text}
							</span>
							{b.section && (
								<Button
									variant="ghost"
									size="sm"
									className="shrink-0 gap-1 text-kaefer-sapphire-blue"
									onClick={() => onGo(b.section as SectionKey)}
								>
									{sectionLabel(b.section)} <ArrowRight className="size-3.5" />
								</Button>
							)}
						</li>
					))}
				</ul>
			)}

			{deniedByProfile && (
				<p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
					<Lock className="size-3.5" />
					Você está como {PROFILE_LABEL[profile]}; esta etapa é feita por{' '}
					{transition.who}.
				</p>
			)}
		</section>
	)
}
